import { useNavigate } from "@tanstack/react-router";
import { LEAD_RUN_SOURCE_WORDS, LEAD_STATUS_WORDS, type AdminLeadAppliedLabel, type AdminLeadRun, type LeadLabelSport, type LeadSport, type LeadStatus } from "@teamna/shared";
import { Badge, Button, Tabs, type BadgeTone } from "@teamna/ui/components/index.ts";

/** The parts the Lead Finder pages share: the tabs, the words, the score and the label chips. */

export type LeadsView = "leads" | "labels" | "runs" | "settings";

const VIEW_PATHS: Record<LeadsView, "/leads" | "/leads/labels" | "/leads/runs" | "/leads/settings"> = {
  leads: "/leads",
  labels: "/leads/labels",
  runs: "/leads/runs",
  settings: "/leads/settings",
};

export function LeadsTabs({ current }: { current: LeadsView }) {
  const navigate = useNavigate();
  return (
    <Tabs
      label="Lead Finder views"
      selectedId={current}
      onSelect={(id) => void navigate({ to: VIEW_PATHS[id as LeadsView] })}
      tabs={[
        { id: "leads", label: "Leads" },
        { id: "labels", label: "Labels" },
        { id: "runs", label: "Runs" },
        { id: "settings", label: "Settings" },
      ]}
    />
  );
}

export const SPORT_WORDS: Record<LeadSport, string> = { swimming: "Swimming", pilates: "Pilates" };
export const LABEL_SPORT_WORDS: Record<LeadLabelSport, string> = { swimming: "Swimming", pilates: "Pilates", both: "Both" };

const STATUS_TONES: Record<LeadStatus, BadgeTone> = {
  new: "info",
  contacted: "quiet",
  replied: "quiet",
  demo: "late",
  customer: "confirmed",
  lost: "cancelled",
  do_not_contact: "cancelled",
};

export function StatusBadge({ status }: { status: LeadStatus }) {
  return <Badge tone={STATUS_TONES[status]}>{LEAD_STATUS_WORDS[status]}</Badge>;
}

/** "+30", "-100", or nothing for a label with no score. */
export function scoreText(score: number | null): string {
  if (score === null) return "";
  return score > 0 ? `+${score}` : String(score);
}

export function ScoreValue({ score }: { score: number }) {
  return <span className={`text-[16px] font-bold tabular-nums ${score < 0 ? "text-cancelled-ink" : "text-ink"}`}>{score}</span>;
}

/** Who put a label on: Claude, Control Center, or a person's email. */
export function addedByText(label: AdminLeadAppliedLabel): string {
  if (label.addedBy === "claude") return "Claude";
  if (label.addedBy === "system") return "Control Center";
  return label.addedBy;
}

/** A label on a lead. The × takes it off; it then stays off in later runs. */
export function LabelChip({ label, onRemove, busy }: { label: AdminLeadAppliedLabel; onRemove?: () => void; busy?: boolean }) {
  const why = [`Added by ${addedByText(label)}`, label.reason].filter(Boolean).join(": ");
  return (
    <span className="sk-chip inline-flex items-center gap-1.5 rounded-pill py-0.5 pr-1 pl-2.5 text-[12px] font-semibold whitespace-nowrap text-ink" title={why}>
      <span dir="auto">{label.name}</span>
      {label.score !== null ? (
        <span className={`rounded-pill px-1.5 tabular-nums ${label.score < 0 ? "bg-cancelled-surface text-cancelled-ink" : "bg-confirmed-surface text-confirmed-ink"}`}>
          {scoreText(label.score)}
        </span>
      ) : null}
      {onRemove ? (
        <Button variant="ghost" size="xs" onClick={onRemove} disabled={busy} aria-label={`Remove ${label.name}`}>
          ×
        </Button>
      ) : (
        <span className="pr-1.5" />
      )}
    </span>
  );
}

export function runTitle(run: Pick<AdminLeadRun, "number" | "sport" | "emirate" | "source">): string {
  return `#${run.number} ${SPORT_WORDS[run.sport]}, ${run.emirate}, ${LEAD_RUN_SOURCE_WORDS[run.source]}`;
}

/** A WhatsApp link that opens a chat with the number. Nothing is sent until the person sends it. */
export function whatsAppLink(e164: string): string {
  return `https://wa.me/${e164.replace(/\D/g, "")}`;
}

export function instagramLink(handle: string): string {
  return `https://www.instagram.com/${handle}/`;
}
